import currencyData from "constants/currencyConstants";
import durationData from "../data/durationData";
import urlBuilder from "@Utils/urlBuilder";
import durationConfig from "@Config/index";
import getUserLanguageCSR from "helpers/getUserLanguageCSR";
import getStringFromCount from "@Utils/getStringFromCount";
import getCurrencySymbol from "helpers/getCurrencySymbol";
import { isEmptyObject } from "helpers/validations";
import { addDurationToDate } from "@Components/LeadForm/utils";
import { formatDate } from "@Pages/Payment/utils";
import getIconName from "./getIconName";

export { default as getIconName } from "./getIconName";
export { default as getFormattedTags, getArea } from "./getFormattedTags";

// Property offer types - offers directly from the property
const PROPERTY_OFFER_TYPES = [
  "property_offer",
  "amber_cashback_offer",
  "amber_exclusive_property_offer",
] as const;

export interface CategorizedOffer {
  id?: string | number;
  type: string;
  name: string;
  value: number;
}

export interface CalculateDiscountedPriceResult {
  // Existing fields (backward compatible)
  duration: string;
  currency: string;
  only_price: string;
  base_rent: string;
  total_rent_calculation: string;
  total_rent: string;
  offers_array: CategorizedOffer[];
  total_saving: number;
  effective_total_rent: string;
  base_rent_strikethrough: string;
  effective_base_rent: string;
  utility_charges: string;
  only_utility_charges: number | string;
  is_flexible: boolean;
  isMobile: boolean;
  tenancy_id: number | null;

  // New categorized fields
  property_offers_array: CategorizedOffer[];
  property_offers_total: number;
  amber_benefits_array: CategorizedOffer[];
  amber_benefits_total: number;
  is_negative_effective_value: boolean;
}

/**
 * Categorizes an offer based on its type
 * @param offerType - The type of the offer
 * @returns true if the offer is a Property Offer, false if it's an Amber Benefit
 */
const isPropertyOffer = (offerType: string | null | undefined): boolean => {
  if (!offerType) return false;
  return PROPERTY_OFFER_TYPES.includes(
    offerType.toLowerCase() as (typeof PROPERTY_OFFER_TYPES)[number],
  );
};

export const getCurrencySelectOptions = () => {
  const finalCurrencyData = currencyData.map((currencyObject) => ({
    value: currencyObject.short_name,
    name: currencyObject.full_name,
  }));
  return finalCurrencyData;
};

// Get symbol given a currency name
export const getPricingCurrency = (currency: any) => {
  const finalCurrencyObject = currencyData.find(
    (currencyObject) =>
      currencyObject?.short_name?.toLocaleLowerCase() === currency?.toLocaleLowerCase() ||
      currencyObject?.standard_name?.toLocaleLowerCase() === currency?.toLocaleLowerCase(),
  );
  if (finalCurrencyObject) {
    return finalCurrencyObject.symbol;
  }
  return "£";
};

// Get symbol given a currency name
export const getPricingCurrencyStandardName = (currency: any) => {
  const finalCurrencyObject = currencyData.find(
    (currencyObject) =>
      currencyObject.short_name?.toLocaleLowerCase() == currency?.toLocaleLowerCase(),
  );
  if (finalCurrencyObject) {
    return finalCurrencyObject.standard_name;
  }
  return null;
};

// Get currency given a region name
export const getCurrencyFromRegion = (regionName: any) => {
  regionName = regionName || "";
  let currency = "dollar";
  const finalCurrencyObject = currencyData.find((currencyObject) => {
    let flag = false;
    if (currencyObject.countries && currencyObject.countries.length > 0) {
      currencyObject.countries.forEach((country) => {
        if (regionName.toLowerCase().includes(country)) {
          flag = true;
        }
      });
    }
    return flag;
  });
  if (finalCurrencyObject) {
    currency = finalCurrencyObject.short_name;
  }
  return currency;
};

// ======================================================= HELPERS - DURATION
export const getPricingDuration = (duration: string) => {
  let durationFormatted = null;
  const zhLang = durationConfig.IS_CHINA_APP && getUserLanguageCSR() === "zh";
  switch (duration) {
    case "weekly":
      durationFormatted = zhLang ? "周" : "week";
      break;
    case "monthly":
      durationFormatted = zhLang ? "月" : "month";
      break;
    case "daily":
      durationFormatted = zhLang ? "天" : "day";
      break;
    default:
      break;
  }
  return durationFormatted;
};

export const getDurationShort = (duration: any) => {
  let durationFormatted = null;
  switch (duration) {
    case "weekly":
      durationFormatted = "week";
      break;
    case "monthly":
      durationFormatted = "mo";
      break;
    default:
      break;
  }
  return durationFormatted;
};

export const getDurationFromRegion = (regionName: any) => {
  regionName = regionName || "";
  let duration = "monthly";
  const finalDurationObject = durationData.find((durationObject) => {
    let flag = false;
    if (durationObject.countries && durationObject.countries.length > 0) {
      durationObject.countries.forEach((country) => {
        if (regionName.toLowerCase().includes(country)) {
          flag = true;
        }
      });
    }
    return flag;
  });
  if (finalDurationObject) {
    duration = finalDurationObject.formatted;
  }
  return duration;
};

// ======================================================= HELPERS - PRICING
export const getPricingPriceFormatted = (pricingObject: any, options: any) => {
  const pricingArray = [];
  const pricingCurrency = getPricingCurrency(pricingObject.currency);
  const pricingDuration = getPricingDuration(pricingObject.duration);
  // @ts-ignore
  const pricingPrice = getPricingPrice(pricingObject, pricingCurrency);
  const pricingPricePerPerson = getPricingPricePerPerson(pricingObject);
  if (pricingPrice) {
    pricingArray.push(pricingPrice);
  }
  if (pricingDuration) {
    pricingArray.push(pricingDuration);
  }
  if (pricingPricePerPerson && options.price_per_person) {
    pricingArray.push(pricingPricePerPerson);
  }
  if (pricingPrice && pricingDuration) {
    const finalPrice = pricingArray.join("/");
    return finalPrice;
  }
  return "Price on Request";
};

export const getPricingDepositFormatted = (pricingObject: any) => {
  const pricingCurrency = getPricingCurrency(pricingObject.currency);
  const pricingDeposit = pricingObject.deposit;
  if (pricingDeposit || pricingDeposit == 0) {
    return `${pricingCurrency}${pricingDeposit}`;
  }
  return "";
};

/**
 * formatTokenPlatformFees - format token and platform fees for display.
 *
 * @param {string} paymentType - The type of payment to retrieve from the meta object.
 * @param {Object} pricingObject - The pricing object containing currency and other related information.
 * @returns {string} A formatted string representing token platform fees.
 */
export const formatTokenPlatformFees = (paymentType: any, pricingObject: any) => {
  const pricingCurrency = getPricingCurrency(pricingObject.currency);
  const pricingDeposit = pricingObject[paymentType];
  if (pricingDeposit || pricingDeposit == 0) {
    return `${pricingCurrency}${pricingDeposit}`;
  }
  return "";
};

export const getPricingPricePerPerson = (pricingObject: any) => {
  const zhLang = durationConfig.IS_CHINA_APP && getUserLanguageCSR() === "zh";
  if (pricingObject.price_per_person) {
    return zhLang ? "人" : "person";
  }
  return "";
};

export const getPricingPrice = (pricingObject: any) => {
  let price;
  let currency = getPricingCurrency(pricingObject.currency);
  currency = currency || "";
  if (pricingObject?.min_price && pricingObject?.max_price) {
    // if (pricingObject?.min_price != pricingObject?.max_price) {
    //   price = `${
    //     currency + pricingObject.min_price.toLocaleString("en")
    //   } - ${currency}${pricingObject.max_price.toLocaleString("en")}`;
    // }

    // else {
    price = currency + pricingObject.min_price.toLocaleString("en");
    // }
  } else if (pricingObject?.price) {
    price = currency + pricingObject?.price.toLocaleString("en");
  } else if (pricingObject?.min_price) {
    price = `${currency}${pricingObject.min_price.toLocaleString("en")}`;
  }
  return price;
};

export const getStartingPricingHelper = (pricingObject: any) => {
  if (pricingObject?.min_price) {
    return pricingObject.min_price;
  }
  if (pricingObject?.price) {
    return pricingObject.price;
  }
  return undefined;
};

export const getStartingPricing = (pricingObject: any, showPlus = true) => {
  let startingPrice = "";
  const price = getStartingPricingHelper(pricingObject);
  const currency = getPricingCurrency(pricingObject.currency) ?? "";
  if (showPlus && pricingObject?.min_price) {
    startingPrice = `${currency + price}+`;
  } else if (pricingObject?.price) {
    startingPrice = currency + price;
  }
  return startingPrice;
};

export const getAvailableStartingPricingHelper = (pricingObject: any) => {
  if (pricingObject?.min_available_price) {
    return pricingObject.min_available_price;
  }
  if (pricingObject?.available_price) {
    return pricingObject.available_price;
  }
  if (pricingObject?.min_price) {
    return pricingObject.min_price;
  }
  return pricingObject?.price;
};

export const getAvailableStartingPricing = (pricingObject: any) => {
  const price = getAvailableStartingPricingHelper(pricingObject);
  const currency = getPricingCurrency(pricingObject?.currency) ?? "";
  return price ? currency + price : null;
};

// ======================================================= HELPERS - IMAGES

/**
 * @param {Array} images -  List of images for an inventory
 * @returns Array - if any feature image is there moves its at zero index, otherwise returns array as it is.
 */
export const moveFeatureImageAtFirst = (images = []) => {
  const featureImgIndex = images?.findIndex(({ featured }) => featured);
  if (featureImgIndex > 0) {
    const tempImgs = [...images];
    const slicedImages = tempImgs.slice(featureImgIndex, 1);
    const transformedImgsArr = [images[featureImgIndex], ...slicedImages];
    return transformedImgsArr;
  }
  return images;
};

export const getFeaturedImage = (images: any) => {
  images = images || [];
  let featuredImage = images[0];
  images.forEach((image: any) => {
    if (image?.featured?.toString() == "true") {
      featuredImage = image;
    }
  });
  return featuredImage;
};

export const getFeaturedImageAtFirst = (images: any) => {
  if (!images || images.length === 0) return [];
  let newImages = [...images];
  let index = -1;
  let featuredImage: any = null;
  const typeOrder = ["bedroom", "amenities", "kitchen", "bathroom"];

  images.forEach((image: any, idx: number) => {
    if (image?.featured?.toString() == "true" && !featuredImage) {
      index = idx;
      featuredImage = image;
    }
  });

  // @ts-ignore
  newImages = images.filter((img: any, idx: any) => idx !== index);

  newImages.sort((a: any, b: any) => {
    const orderA = typeOrder.indexOf(a?.type?.toLowerCase());
    const orderB = typeOrder.indexOf(b?.type?.toLowerCase());
    if (orderA !== -1 && orderB !== -1) return orderA - orderB;
    if (orderA !== -1) return -1;
    if (orderB !== -1) return 1;
    return 0;
  });

  if (index >= 0) {
    return [featuredImage, ...newImages];
  }

  return newImages;
};

// returns all the inventory (parent), config (child) level images
export const getAllImages = (data: any) => {
  if (!data) return;
  const images = [...data?.images];
  const featuredImage = getFeaturedImage(data?.images);
  data.children_short?.forEach((child: any) => {
    child.images?.forEach((image: any) => {
      if (!featuredImage || featuredImage.base_path !== image.base_path) {
        images.push({ ...(image || {}), level: "child" });
      }
    });
  });
  // eslint-disable-next-line consistent-return
  return images;
};

export const isAvailable = (data: any) => {
  let flag = true;
  // Either if tag is mentioned
  if (data?.tags) {
    data.tags?.forEach((tag: any) => {
      if (tag == "not_available") {
        flag = false;
      }
    });
  }

  // if inventory is not available
  if (data?.available === false) {
    flag = false;
  }

  // Otherwise if its inactive (only for parent)
  if (data?.parent_id == null && data?.status == "inactive") {
    flag = false;
  }
  return flag;
};

type TranslateFn = (key: string, value?: Record<string, string>, options?: any) => string;

// Full month names mapped by JS month index (0 = January). These map to the
// standalone translation keys present in src/locales/translations.json.
const MONTH_TRANSLATION_KEYS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

/**
 * Returns the localized month name for a given date. Falls back to the English
 * short month name when no translator is provided or the key can't be resolved.
 */
export const getTranslatedMonth = (date: Date, t?: TranslateFn): string => {
  const fallback = date.toLocaleString("default", { month: "short" });
  if (!t) return fallback;

  const key = MONTH_TRANSLATION_KEYS[date.getMonth()];
  const translated = key ? t(key) : "";
  return translated || fallback;
};

// Short English month names, matching the `%b` the backend uses for tenancy
// `available_from_formatted` (and not the runtime locale, which varies by browser).
const SHORT_MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Formats a date as "19 Sep, 2026" — the same shape as the backend's
 * `available_from_formatted` ("%-d %b, %Y"), so a room type's "Available from"
 * matches the dates on its tenancy rows. China re-formats it via `chinaFormattedDate`.
 */
export const formatDateString = (date: Date): string =>
  `${date.getDate()} ${SHORT_MONTH_NAMES[date.getMonth()]}, ${date.getFullYear()}`;
function parseDynamicDate(input: any) {
  // Split the input string by hyphen to get parts
  const parts = input.split("-");

  let day;
  let month;
  let year;

  if (parts.length === 3) {
    if (parts[0].length === 4) {
      // YYYY MM DD format
      [year, month, day] = parts;
    } else {
      // DD MM YYYY format
      [day, month, year] = parts;
    }

    // Create a new Date object with year, month, and day
    // Note: Month is 0-indexed in JavaScript Date (0 = January, 11 = December)
    const date = new Date(year, month - 1, day);

    // Check if the date is valid
    if (date.getDate() != day || date.getMonth() != month - 1 || date.getFullYear() != year) {
      throw new Error("Invalid date");
    }

    return date;
  }
  throw new Error("Invalid input format");
}

/** Earliest start among the bookable tenancies listed ("today" for one with no start date), or "". */
const earliestListedStart = (children: Record<string, any>[] = []): string => {
  if (children.some((item) => item.available && !item?.meta?.available_from)) {
    return formatDateString(new Date());
  }
  const dates = children
    .filter((item) => item.available && item?.meta?.available_from)
    .map((item) => parseDynamicDate(item.meta.available_from));
  return dates.length
    ? formatDateString(dates.reduce((min, d) => (d < min ? d : min), dates[0]))
    : "";
};

/**
 * `moveInMonth`: the property page's applied move-in month (P1D-5529). With a month applied the
 * date is the earliest start among the tenancies listed, which only include that month; the
 * room-level date covers every tenancy ("Available from 14 Oct" above 29 Dec tenancies). Without a
 * month the original logic below runs unchanged.
 */
export const getMinimumAvailableFromValue = (
  available: boolean,
  meta: any,
  children: Record<string, any>[],
  moveInMonth?: string,
): string => {
  if (!available) return "";

  if (moveInMonth) {
    const listed = earliestListedStart(children || []);
    if (listed) return listed;
  }

  // If meta has available_from or min_available_from, use it
  if (meta?.available_from || meta?.min_available_from) {
    const isFlexible = (children || []).some(
      (item) => item.available && !item?.meta?.available_from,
    );
    if (isFlexible) {
      return formatDateString(new Date());
    }
    if (children.length === 0) {
      // No children data but meta has date — use meta directly
      if (meta?.min_available_from) {
        return formatDateString(parseDynamicDate(meta.min_available_from));
      }
      return meta.available_from_formatted || "";
    }
    if (meta?.min_available_from) {
      return formatDateString(parseDynamicDate(meta.min_available_from));
    }
    return meta.available_from_formatted;
  }

  // Fallback: compute min available_from from children (tenancies)
  if (children && children.length > 0) {
    const hasFlexibleChild = children.some((item) => item.available && !item?.meta?.available_from);
    if (hasFlexibleChild) {
      return formatDateString(new Date());
    }
    const availableChildren = children.filter(
      (item) => item.available && item?.meta?.available_from,
    );
    if (availableChildren.length > 0) {
      const dates = availableChildren.map((item) => parseDynamicDate(item.meta.available_from));
      const minDate = dates.reduce((min, d) => (d < min ? d : min), dates[0]);
      return formatDateString(minDate);
    }
  }

  return "";
};

export const chinaFormattedDate = (minAvailableFrom: string, t?: TranslateFn): string => {
  if (!minAvailableFrom) return "";

  const date = new Date(minAvailableFrom);
  if (Number.isNaN(date.getTime())) return minAvailableFrom;

  const year = date.getFullYear();
  const month = t ? getTranslatedMonth(date, t) : date.toLocaleString("en-US", { month: "long" });
  const day = date.getDate();

  return `${year}, ${month} ${day}`;
};

// Get the full lease duration from Meta
export const getLeaseDuration = (metaObject: any) => {
  let leaseDuration = null;
  if (metaObject?.lease_duration > 0 && metaObject.lease_duration_unit) {
    leaseDuration = getStringFromCount(metaObject.lease_duration, metaObject.lease_duration_unit);
  }
  return leaseDuration;
};

export const getMinMaxLeaseDuration = (metaObject: any) => {
  let leaseDuration = null;
  if (metaObject?.min_lease_duration > 0 && metaObject.lease_duration_unit) {
    if (!leaseDuration) {
      leaseDuration = "";
    }
    leaseDuration += getStringFromCount(metaObject.lease_duration, metaObject.lease_duration_unit);
  }
  if (metaObject?.max_lease_duration > 0 && metaObject.lease_duration_unit) {
    if (!leaseDuration) {
      leaseDuration = "";
    } else {
      leaseDuration += " - ";
    }
    leaseDuration += getStringFromCount(metaObject.lease_duration, metaObject.lease_duration_unit);
  }
  return leaseDuration;
};

export const getLeaseDurationFormatted = (metaObject: any) => {
  let finalLeaseDuration = null;
  const leaseDuration = getLeaseDuration(metaObject);
  const leaseDurationRange = getMinMaxLeaseDuration(metaObject);
  if (leaseDuration) {
    finalLeaseDuration = leaseDuration;
  }
  if (leaseDurationRange) {
    finalLeaseDuration = leaseDurationRange;
  }
  return finalLeaseDuration;
};

export const getAvailableFrom = (metaObject: any) => {
  let availableFrom = null;
  if (metaObject.available_from?.length > 0) {
    availableFrom = metaObject.available_from;
  }
  if (metaObject.available_from_formatted?.length > 0) {
    availableFrom = metaObject.available_from_formatted;
  }
  return availableFrom;
};

export const getAvailableUptoDate = ({ leaseDuration, availableFrom }: any) => {
  if (!availableFrom || !leaseDuration) return "";

  const [durationValStr, durationUnit] = leaseDuration.split(" ");
  const durationVal = Number(durationValStr);

  if (Number.isNaN(durationVal)) return "";

  const endDate = addDurationToDate(availableFrom, durationVal, durationUnit);

  const availableUptoDate = endDate.getDate();
  const availableUptoMonth = new Intl.DateTimeFormat("en-US", { month: "short" }).format(endDate);
  const availableUptoYear = endDate.getFullYear();

  return `${availableUptoDate} ${availableUptoMonth}, ${availableUptoYear}`;
};

export const getAvailableTo = (metaObject: any) => {
  let availableTo = null;
  if (metaObject.available_to?.length > 0) {
    availableTo = formatDate(metaObject?.available_to, true);
  }
  if (metaObject.available_to_formatted?.length > 0) {
    availableTo = metaObject.available_to_formatted;
  }
  return availableTo;
};

/** "13 m2" from a tenancy's `meta.area` + `meta.area_unit` (default sqft, as `getFormattedTags`). */
export const getTenancyAreaLabel = (meta: any): string =>
  meta?.area ? `${meta.area} ${meta.area_unit || "sqft"}` : "";

export const formatTenancyDetails = (tenancy: any) => ({
  isAvailable: isAvailable(tenancy),
  price: getPricingPriceFormatted(tenancy.pricing, { price_per_person: true }),
  duration: getLeaseDurationFormatted(tenancy.meta),
  moveInDate: getAvailableFrom(tenancy.meta),
  moveOutDate: getAvailableTo(tenancy.meta),
  floor: tenancy.meta?.floor,
  facing: tenancy.meta?.facing,
  // "13 m2" from `meta.area` + `meta.area_unit` (default sqft, as `getFormattedTags`).
  area: tenancy.meta?.area ? `${tenancy.meta.area} ${tenancy.meta.area_unit || "sqft"}` : "",
});

export const unitTypes: any = {
  all: "All",
  ensuite: "Ensuite",
  non_ensuite: "Non Ensuite",
  studio: "Studio",
  shared_room: "Shared Room",
  twin_studio: "Twin Studio",
  twin_ensuite: "Twin Ensuite",
  private_room: "Private Room",
  "1b": "1B",
  "2b": "2B",
  "3b": "3B",
  "4b": "4B",
  "5b": "5B",
  "6b": "6B",
  "7b": "7B",
  "8b": "8B",
  greater_8b: ">8B",
};

export const sortTenancies = (tenancies: any[]) => {
  const sortedTenancies = [...(tenancies || [])];

  // Helper to get effective price for a tenancy
  const getEffectivePrice = (tenancy: any): number => {
    const slashPrice = tenancy?.pricing?.slashed_pricing?.effective_base_rent;
    // If slash price exists and is a valid number, use it
    if (slashPrice !== null && slashPrice !== undefined && !Number.isNaN(slashPrice)) {
      return slashPrice;
    }
    // Otherwise use available_price or price
    return tenancy?.pricing?.available_price ?? tenancy?.pricing?.price ?? 0;
  };

  sortedTenancies.sort((item1, item2) => {
    const item1Available = isAvailable(item1);
    const item2Available = isAvailable(item2);

    // First priority: available tenancies come before unavailable
    if (item1Available && !item2Available) return -1;
    if (!item1Available && item2Available) return 1;

    // Second priority (only for available tenancies): sort by effective price (lower first)
    if (item1Available && item2Available) {
      const price1 = getEffectivePrice(item1);
      const price2 = getEffectivePrice(item2);

      return price1 - price2;
    }

    return 0;
  });

  return sortedTenancies;
};

export const getAvailableTenancyCount = (tenancies: any[]) => {
  const allTenancies = [...(tenancies || [])];
  let count = 0;

  allTenancies.forEach((tenancy) => {
    if (isAvailable(tenancy)) {
      count += 1;
    }
  });

  return count;
};

export const getAvailableTenancy = (tenancies: any[]) => {
  const available: any[] = tenancies.filter((tenancy) => isAvailable(tenancy));
  const unavailable: any[] = tenancies.filter((tenancy) => !isAvailable(tenancy));
  return [available, unavailable];
};

export const getFormattedGalleryAssets = (data: any, includeVideos = true) => {
  const roomAssets = data?.images.map((image: any) => ({
    ...image,
    configName: data.name,
    category: "image",
  }));

  // Videos count towards the card badge, as they ship in the room gallery. Whitelabel has
  // no room gallery, so it keeps the old photos + 3D count.
  if (includeVideos && data.videos?.length > 0) {
    data.videos.forEach((video: any) =>
      roomAssets.push({ ...video, configName: data.name, category: "videos" }),
    );
  }
  if (data.virtual_views?.length > 0) {
    data.virtual_views.forEach((virtualView: any) =>
      roomAssets.push({ ...virtualView, configName: data.name, category: "3D" }),
    );
  }
  return roomAssets;
};

// export const getPaymentType = (inventory: any) => inventory.meta?.payment_type || "deposit";

export const getDepositTextLabel = (paymentType: any) => {
  switch (paymentType) {
    case "deposit":
      return "Deposit";
    // case 'refundable_token':
    //   return 'Fully Refundable Holding Fee'
    // case 'platform_fee':
    //   return 'Platform Fee'
    default:
      return "";
  }
};

export const getFormattedFeatures = (data: any): any => {
  const features = data.features?.length
    ? data.features.map((feature: any) => ({
        featureTitle: feature.name,
        values: feature.values.map(({ name, type }: any) =>
          name ? { icon: getIconName(name), text: name } : { icon: getIconName(type), text: type },
        ),
      }))
    : [];

  const unitAmenitiesFeature = features.find(
    ({ featureTitle }: any) => featureTitle == "Unit Amenities",
  );

  if (unitAmenitiesFeature) {
    return [
      unitAmenitiesFeature,
      ...features.filter(({ featureTitle }: any) => featureTitle !== "Unit Amenities"),
    ];
  }
  return features;
};

export const getNewLeadFormPageRedirectURL = (inventory_object: any) => {
  const selectedTennacy = (
    inventory_object?.config?.active_children ||
    inventory_object?.config?.active_children_with_filters ||
    []
  ).filter((config: any) => config.id === inventory_object.id)[0];
  const url_options: any = {};
  url_options.path = `${window?.location?.origin}${window?.location?.pathname}/lead`;
  url_options.params = {
    inventory_id: inventory_object?.id || inventory_object?.config?.id,
  };
  if (inventory_object?.config?.name)
    url_options.params["config_name"] = inventory_object?.config?.name;
  if (selectedTennacy?.meta?.lease_duration) {
    url_options.params[
      "lease_duration"
    ] = `${selectedTennacy?.meta?.lease_duration} ${selectedTennacy?.meta?.lease_duration_unit}`;
  }
  if (selectedTennacy?.meta?.available_from)
    url_options.params["move_in_date"] = selectedTennacy?.meta?.available_from;
  url_options.params["bookform_source"] = "book_tenancy";
  return urlBuilder(url_options.path, { params: url_options.params });
};

export const getFormFullUrl = (inventory: any, params = {}) => {
  const { canonical_name } = inventory;
  const paths = ["places", canonical_name, "form"];
  // Build link
  const url = urlBuilder(paths, { params });
  return url;
};

export const setFormUrl = (inventory: any, postSuccessData: any) => {
  const options: any = {};
  // set inventory id (if parent_id exist -> tenancy id)
  if (inventory?.parent_id && inventory?.id === inventory?.config?.id) {
    options.inventory_id = inventory?.children[0]?.id ?? inventory?.id;
  } else {
    options.inventory_id = inventory?.id;
  }

  // resetMoveInDate flag comes from SectionChildrenOldV2(Contact us for different dates)
  // It means tenancy shouldn't be selected by default, so setting id of config(room)
  if (inventory.resetMoveInDate) {
    options.inventory_id = inventory?.id;
  }

  // set lead id
  if (postSuccessData?.id) options.lead_id = postSuccessData.id;

  // set lead uuid
  if (postSuccessData?.uuid) options.lead_uuid = postSuccessData.uuid;

  // Add config name
  if (postSuccessData?.data?.configuration)
    options.config_name = postSuccessData.data.configuration;

  // Add config name
  if (inventory?.config?.name) options.config_name = inventory.config.name;

  // Add university
  if (postSuccessData?.data?.university) options.university = postSuccessData.data.university;

  // Set lease duration
  if (postSuccessData?.data?.lease_duration)
    options.lease_duration = postSuccessData.data.lease_duration;

  // Set move in date
  if (postSuccessData?.data?.move_in_date) options.move_in_date = postSuccessData.data.move_in_date;

  // Set user email
  if (postSuccessData?.user_details?.email) options.email = postSuccessData.user_details.email;

  if (postSuccessData?.user_details?.phone_details?.country_code)
    options["phone[first]"] = postSuccessData.user_details.phone_details.country_code;

  if (postSuccessData?.user_details?.phone_details?.national)
    options["phone[last]"] = postSuccessData.user_details.phone_details.national;

  if (postSuccessData?.user_details?.name) {
    const fullName = postSuccessData.user_details.name.split(" ");
    // eslint-disable-next-line prefer-destructuring
    options["fullName[first]"] = fullName[0];
    fullName.shift();
    options["fullName[last]"] = fullName.join(" ");
  }
  return getFormFullUrl(inventory.canonical_name, options);
};

export const getRedirectURL = (inventory: any, postSuccessData: any) =>
  setFormUrl(inventory, postSuccessData) || "/";

export const getRoomTypeTabData = (data: any, addUnitTypeConvertor = false) => {
  const tabData: any = {
    all: data,
  };
  data.forEach((item: any) => {
    const unitType = addUnitTypeConvertor
      ? unitTypes[item?.meta?.unit_type]
      : item?.meta?.unit_type;
    if (tabData[unitType]) {
      tabData[unitType].push(item);
    } else {
      tabData[unitType] = [item];
    }
  });
  return tabData;
};
export const getCustomData = (data: any, activeTab: any) => {
  const tabsData: any = [];
  Object.keys(data).forEach((item: any) => {
    if (activeTab !== "all" && item === activeTab) {
      const tabData: any = {};
      tabData.tab = item;
      tabData.data = data[item];
      tabsData.push(tabData);
    } else if (item !== "all" && activeTab === "all") {
      const tabData: any = {};
      tabData.tab = item;
      tabData.data = data[item];
      tabsData.push(tabData);
    }
  });
  return tabsData;
};
export const filterAvailableRooms = (rooms: any, id: number) => {
  const idIndex = rooms.findIndex((r: any) => r.id === id);
  const sortedRooms = [...rooms.slice(idIndex, rooms.length), ...rooms.slice(0, idIndex)];
  const filteredRooms = sortedRooms.filter((r: any) => r.id != id && isAvailable(r));
  return filteredRooms.slice(0, 2);
};

export const getSoldOutRooms = (rooms: any) => {
  const availableRooms = rooms.filter((r: any) => isAvailable(r));
  const soldOutRooms = rooms.filter((r: any) => !isAvailable(r));
  if (soldOutRooms.length > 0) {
    const firstElement = soldOutRooms.shift(); // Remove the first element from arr2
    availableRooms.push(firstElement); // Add it to the end of arr1
  }
  return [availableRooms, soldOutRooms];
};

export const calculateDiscountedPrice = ({
  // 1. INPUT/PROPS
  tenancy = {},
  price = "0/Weekly",
  propertyData = {},
  duration = "0 weeks",
  roomTypeData = {},
  isDesktop = false,
  showBillsIncluded,
}: any) => {
  // 2. GLOBAL/UTILITY VARIABLES
  const isFlexible = typeof tenancy !== "undefined" && isEmptyObject(tenancy);
  const currencyCode = propertyData?.pricing?.currency || "GBP";
  const currencySymbol = getCurrencySymbol(currencyCode);

  // 3. PRICE/DURATION CALCULATION
  const [rawPrice = "0", priceInterval = "week", person = ""] = price?.split("/") ?? [];
  const durationValue = parseInt(duration?.split(" ")[0], 10) || 0;
  const pricing = tenancy?.pricing || {};
  const availablePrice =
    pricing?.available_price ||
    pricing?.min_available_price ||
    pricing?.min_price ||
    pricing?.price ||
    0;
  const isWeekly = pricing?.duration === "weekly" || priceInterval === "week";
  const priceDurationLabel = isWeekly ? "Weekly" : "Monthly";
  const totalRent = availablePrice * durationValue;
  const totalRentCalculation = `${rawPrice}x${durationValue} ${priceInterval}s`;

  // 4. OFFERS/DISCOUNTS
  const percentageDiscountOnTotalRent = (value: number, value_type: string) => {
    if (isFlexible) return value;
    return value_type === "percentage" ? (totalRent * value * 0.01).toFixed(2) : value;
  };
  const offersForInvArray = propertyData?.active_offers?.filter(
    (offer: any) =>
      offer?.is_blanket_offer_for_frontend === true && offer?.add_offer_on_all_tenancies === true,
  );
  const offersForTenancy = propertyData?.active_offers?.filter(
    (offer: any) =>
      offer?.is_blanket_offer_for_frontend === true &&
      offer?.applied_on_tenancies_ids?.includes(tenancy?.id) &&
      offer?.add_offer_on_all_tenancies !== true,
  );
  const getofferForConfig = () => {
    if (roomTypeData?.active_children_count === 0) {
      const data = propertyData?.active_offers?.filter(
        (offer: any) =>
          offer?.is_blanket_offer_for_frontend === true &&
          offer?.applied_on_tenancies_ids?.includes(roomTypeData?.id),
      );
      return data;
    }
    return [];
  };
  const getOffersForSlashPricing = () => {
    if (!isFlexible && tenancy?.available)
      return [...(offersForInvArray || []), ...(offersForTenancy || [])];
    if (isFlexible) return [...(getofferForConfig() || [])];
    return [];
  };

  // add additional filteration for fetching max property offer and property offer amber exclusive

  const filterMaxPOAndPOAEOffers = (offersList: any[]) => {
    // property_offer and amber_exclusive_property_offer are treated as a single
    // pool — we surface only the single highest-value offer across BOTH types
    // (not the max of each type individually).
    // Uses resolved value (percentage resolved against totalRent) to pick the winner.
    // Offers with resolved value > 1500 are ignored (capped).
    const DISCOUNT_CAP = 1500;
    const propertyOfferPool: any[] = [];
    const otherOffers: any[] = [];

    offersList.forEach((offer: any) => {
      const type = offer?.type?.toLowerCase();
      if (type === "property_offer" || type === "amber_exclusive_property_offer") {
        propertyOfferPool.push(offer);
      } else {
        otherOffers.push(offer);
      }
    });

    const maxOffers: any[] = [];
    if (propertyOfferPool.length) {
      // Resolve each offer's effective value (percentage → absolute using totalRent)
      const resolveValue = (o: any) => {
        const val = Number(o?.value) || 0;
        return o?.value_type === "percentage" ? totalRent * val * 0.01 : val;
      };
      // Filter out offers exceeding the discount cap
      const cappedPool = propertyOfferPool.filter((o: any) => resolveValue(o) <= DISCOUNT_CAP);
      if (cappedPool.length) {
        const maxValue = Math.max(...cappedPool.map(resolveValue));
        const topOffers = cappedPool.filter((o: any) => resolveValue(o) === maxValue);
        // Tie-breaker: pick the lowest id for a deterministic winner.
        topOffers.sort((a: any, b: any) => (Number(a?.id) || 0) - (Number(b?.id) || 0));
        if (topOffers.length) maxOffers.push(topOffers[0]);
      }
    }

    return [...maxOffers, ...otherOffers];
  };

  // Filter and aggregate offers
  const DISCOUNT_CAP = 1500;
  const aggregatedOffers = filterMaxPOAndPOAEOffers(getOffersForSlashPricing())
    .filter(
      (offer: any) =>
        offer?.is_blanket_offer_for_frontend === true &&
        offer?.value !== null &&
        offer?.value !== 0,
    )
    .map((offer: any) => ({
      id: offer?.id,
      type: offer?.type,
      name: offer?.name_zh ? offer?.name_zh : offer?.name,
      value: parseFloat(
        Number(percentageDiscountOnTotalRent(offer?.value, offer?.value_type)).toFixed(2),
      ),
    }))
    .filter((offer: any) => offer?.value <= DISCOUNT_CAP);
  const offers = Object.values(aggregatedOffers) || [];
  const totalSaving = parseFloat(
    offers?.reduce((sum: number, offer: any) => sum + (offer?.value || 0), 0).toFixed(2),
  );
  // Categorize offers into Property Offers and Amber Benefits
  const propertyOffersArray: CategorizedOffer[] = aggregatedOffers.filter(
    (offer: CategorizedOffer) => isPropertyOffer(offer.type),
  );
  const amberBenefitsArray: CategorizedOffer[] = aggregatedOffers.filter(
    (offer: CategorizedOffer) => !isPropertyOffer(offer.type),
  );

  // Calculate category totals
  const propertyOffersTotal = parseFloat(
    propertyOffersArray
      .reduce((sum: number, offer: CategorizedOffer) => sum + (offer.value || 0), 0)
      .toFixed(2),
  );
  const amberBenefitsTotal = parseFloat(
    amberBenefitsArray
      .reduce((sum: number, offer: CategorizedOffer) => sum + (offer.value || 0), 0)
      .toFixed(2),
  );

  // 5. FINAL COMPUTED VALUES
  const effectiveTotalRent = parseFloat((totalRent - totalSaving).toFixed(2));
  const daysInPeriod = durationValue;
  const dailyEffectiveRent = Math.round(effectiveTotalRent / daysInPeriod);
  const isNegativeEffectiveValue = dailyEffectiveRent < 1;
  const effectiveBaseRentText = `${currencySymbol}${dailyEffectiveRent.toFixed(
    0,
  )}/${priceInterval}${person ? `/${person}` : ""}`;
  const billsIncluded = propertyData?.features?.find(
    (feat: any) => feat.type === "bills_included" || feat.type_en === "bills_included",
  );
  const hasUndefinedUtilityCharges = billsIncluded?.values?.some(
    (feat: any) => feat?.price === undefined,
  );
  const utilityCharges = billsIncluded?.values
    ?.reduce((sum: number, feat: any) => sum + (Number(feat?.price) || 0), 0)
    .toFixed(0);

  // 6. RETURN
  return {
    duration: priceDurationLabel,
    currency: currencySymbol,
    only_price: rawPrice,
    base_rent: price,
    total_rent_calculation: totalRentCalculation,
    total_rent: `${currencySymbol}${totalRent}`,
    offers_array: offers,
    total_saving: totalSaving,
    effective_total_rent: `${currencySymbol}${effectiveTotalRent}`,
    base_rent_strikethrough: rawPrice,
    effective_base_rent: effectiveBaseRentText,
    utility_charges: `${currencySymbol}${utilityCharges}${
      hasUndefinedUtilityCharges ? "+" : ""
    }/${priceInterval}`,
    only_utility_charges: showBillsIncluded ? utilityCharges : 0,
    is_flexible: isFlexible,
    isMobile: !isDesktop,
    tenancy_id: tenancy?.id || roomTypeData?.id || null,
    // New categorized fields
    property_offers_array: propertyOffersArray,
    property_offers_total: propertyOffersTotal,
    amber_benefits_array: amberBenefitsArray,
    amber_benefits_total: amberBenefitsTotal,
    is_negative_effective_value: isNegativeEffectiveValue,
  };
};
